import { payFromAccount } from '../budget/accounts';
import { resolveDeductFund } from '../budget/settings';
import type { LocalDate } from '../dates';
import type { DomainEvent, EventOf } from '../events';
import { singletonId } from '../ids';
import { deleteMutation, insertMutation, updateMutation } from '../mutations';
import { defineRule, type Rule, type RuleContext, type RuleResult } from './engine';

/** The Cigarettes category's key; Quit Smoking creates it on seededId(u, 'category', 'cigarettes'). */
export const CIGARETTES_KEY = 'cigarettes';

/**
 * One Cigarettes expense per local day (conventions §3, derived per-day ledger rows):
 * 'u:ledger_entries.quit_smoking.2026-11-02'. Two offline devices converge on one row.
 */
export function quitDayExpenseId(userId: string, date: LocalDate): string {
  return singletonId(userId, `ledger_entries.quit_smoking.${date}`);
}

export function cigaretteNote(count: number): string {
  return count === 1 ? '1 cigarette' : `${count} cigarettes`;
}

const AUTO_NOTE = /^\d+ cigarettes?$/;

/** The note is ours to keep in step while it is empty or still the one this rule wrote. */
function isAutoNote(note: string | null): boolean {
  return note === null || note.trim() === '' || AUTO_NOTE.test(note.trim());
}

/** A time strictly after `after`, so this write wins last-write-wins over the one it follows. */
function laterThan(now: string, after: string): string {
  const t = Date.parse(after);
  return Date.parse(now) > t ? now : new Date(t + 1).toISOString();
}

export type SmokeEvent =
  EventOf<'smoke.logged'> | EventOf<'smoke.total_set'> | EventOf<'smoke.removed'>;

/**
 * Spec §8: smoke.logged writes a Cigarettes expense; smoke.total_set adjusts that day's expense.
 * The event carries the day's count and cost after the change (spec §7 day count), so the one row
 * for that day is set to match and nothing is ever counted twice:
 * - nothing to charge (count 0 or no price): soft-delete a live row;
 * - live row: update the amount (and the note while it is still the automatic "7 cigarettes").
 *   Budget follows on its own, since fund balances are derived from the row's fundId;
 * - no row, or a soft-deleted one: insert it with every field, the fund included
 *   (resolveDeductFund, so nothing waits on a later write that must win under server clock
 *   clamping), and emit expense.created. A plain insert on the deleted id restores it
 *   (conventions §14), as a fresh expense for the day.
 * The category, method and fund of a live row are never touched, so edits made in Spending stay.
 */
export function dayExpenseChange(event: SmokeEvent, ctx: RuleContext): RuleResult {
  const rowId = quitDayExpenseId(ctx.userId, event.date);
  const existing = ctx.read.getQuitDayExpense(event.date);
  const base = { table: 'ledger_entries', source: 'quit_smoking' as const };
  const at = laterThan(ctx.now, event.at);

  if (event.dayCount <= 0 || event.dayCostMinor <= 0) {
    return existing && !existing.deleted ? [deleteMutation({ ...base, rowId, at })] : [];
  }
  const note = cigaretteNote(event.dayCount);
  if (existing && !existing.deleted) {
    const values: Record<string, unknown> = { amountMinor: event.dayCostMinor };
    if (isAutoNote(existing.note) && existing.note !== note) values.note = note;
    return [updateMutation({ ...base, rowId, at, values })];
  }

  // No row, or a soft-deleted one (the day went back to zero): write it fresh with every field.
  // On a deleted row the plain insert restores it (conventions §14).
  const plan = ctx.read.getQuitPlan();
  const categoryId = ctx.read.getCategoryByKey(CIGARETTES_KEY)?.id ?? null;
  const fundId = resolveDeductFund(
    plan?.fundId,
    ctx.read.getBudgetSettings(),
    ctx.usable('budget'),
  );
  // v0.5: the fund's default account, else Budget's "Pay from" account (changeable in Spending).
  const accountId = payFromAccount(
    null,
    fundId ? (ctx.read.getFund?.(fundId) ?? null) : null,
    ctx.read.getBudgetSettings(),
    ctx.usable('budget'),
    ctx.read.getAccounts?.(),
  );
  const insert = insertMutation({
    ...base,
    rowId,
    at,
    values: {
      date: event.date,
      amountMinor: event.dayCostMinor,
      currency: 'PHP',
      kind: 'expense',
      categoryId,
      method: null,
      fundId,
      accountId,
      sourceModule: 'quit_smoking',
      sourceId: plan?.id ?? null,
      note,
      recordedTz: ctx.tz,
    },
  });
  const created: DomainEvent = {
    type: 'expense.created',
    expenseId: rowId,
    amountMinor: event.dayCostMinor,
    categoryId,
    fundId,
    date: event.date,
    method: null,
    sourceModule: 'quit_smoking',
    at,
    mutationId: insert.id,
  };
  return { mutations: [insert], events: [created] };
}

/**
 * Budget's balances are derived from the row, so a later change to a day row that already deducts
 * from a fund touches Budget too. budget.deduct only runs on expense.created, so this rule adds
 * Budget to the connection chain ("Quit Smoking ┄ Spending ┄ Budget") without writing anything.
 */
export function dayExpenseBudgetLink(event: SmokeEvent, ctx: RuleContext): RuleResult {
  if (event.dayCount <= 0 || event.dayCostMinor <= 0) return [];
  const existing = ctx.read.getQuitDayExpense(event.date);
  if (!existing || existing.deleted || !existing.fundId) return [];
  return { mutations: [], connected: true };
}

const EXPENSE_TOUCHES: ['quit_smoking', 'spending'] = ['quit_smoking', 'spending'];
const BUDGET_TOUCHES: ['quit_smoking', 'spending', 'budget'] = [
  'quit_smoking',
  'spending',
  'budget',
];

export const quitCigaretteExpenseRule = defineRule<'smoke.logged'>({
  id: 'quit.cigarette-expense',
  on: 'smoke.logged',
  touches: EXPENSE_TOUCHES,
  run: dayExpenseChange,
});

export const quitCigaretteTotalRule = defineRule<'smoke.total_set'>({
  id: 'quit.cigarette-expense-total',
  on: 'smoke.total_set',
  touches: EXPENSE_TOUCHES,
  run: dayExpenseChange,
});

export const quitCigaretteRemovedRule = defineRule<'smoke.removed'>({
  id: 'quit.cigarette-expense-removed',
  on: 'smoke.removed',
  touches: EXPENSE_TOUCHES,
  run: dayExpenseChange,
});

function budgetLinkRule(on: SmokeEvent['type']): Rule {
  return {
    id: `quit.cigarette-budget.${on}`,
    on,
    touches: BUDGET_TOUCHES,
    run: (e, c) => dayExpenseBudgetLink(e as SmokeEvent, c),
  };
}

/** Every Quit Smoking rule, in the order the web app runs them. */
export const QUIT_RULES: Rule[] = [
  quitCigaretteExpenseRule,
  quitCigaretteTotalRule,
  quitCigaretteRemovedRule,
  budgetLinkRule('smoke.logged'),
  budgetLinkRule('smoke.total_set'),
  budgetLinkRule('smoke.removed'),
];
